import { getRequestOptions } from '../helpers/Fetchwrapper'

class PaymentReceiveService {
    static API_BASE_URL = process.env.REACT_APP_API_URL

    /**
     * Get payment receive list with filtering and pagination
     */
    static async getPaymentReceiveList(params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page || 1,
                per_page: params.per_page || 10,
                search: params.search || '',
                status: params.status || 'all',
                payment_type: params.payment_type || 'all',
                sort_by: params.sort_by || 'create_date',
                sort_order: params.sort_order || 'desc',
                ...params
            })

            const response = await fetch(`${this.API_BASE_URL}payments/receive/list?${queryParams}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching payment receive list:', error)
            throw error
        }
    }

    /**
     * Get payment receive details by ID
     */
    static async getPaymentReceiveDetails(paymentId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}payments/receive/details/${paymentId}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching payment receive details:', error)
            throw error
        }
    }

    /**
     * Create new payment receive
     */
    static async createPaymentReceive(paymentData) {
        try {
            const formData = new FormData()
            
            // Add all payment data to form
            Object.keys(paymentData).forEach(key => {
                if (key === 'file' && paymentData[key]) {
                    formData.append('file', paymentData[key])
                } else if (paymentData[key] !== null && paymentData[key] !== undefined) {
                    formData.append(key, paymentData[key])
                }
            })

            const response = await fetch(`${this.API_BASE_URL}payments/receive/create`, {
                method: 'POST',
                headers: {
                    ...getRequestOptions().headers,
                    // Remove Content-Type to let browser set it with boundary for FormData
                },
                body: formData
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error creating payment receive:', error)
            throw error
        }
    }

    /**
     * Update payment receive
     */
    static async updatePaymentReceive(paymentId, paymentData) {
        try {
            const formData = new FormData()
            
            // Add all payment data to form
            Object.keys(paymentData).forEach(key => {
                if (key === 'file' && paymentData[key]) {
                    formData.append('file', paymentData[key])
                } else if (paymentData[key] !== null && paymentData[key] !== undefined) {
                    formData.append(key, paymentData[key])
                }
            })

            const response = await fetch(`${this.API_BASE_URL}payments/receive/update/${paymentId}`, {
                method: 'POST',
                headers: {
                    ...getRequestOptions().headers,
                    // Remove Content-Type to let browser set it with boundary for FormData
                },
                body: formData
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error updating payment receive:', error)
            throw error
        }
    }

    /**
     * Delete payment receive
     */
    static async deletePaymentReceive(paymentId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}payments/receive/${paymentId}`, {
                method: 'DELETE',
                headers: {
                    ...getRequestOptions().headers
                }
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error deleting payment receive:', error)
            throw error
        }
    }

    /**
     * Get order details for payment receive creation
     */
    static async getOrderForPaymentReceive(orderId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}payments/receive/order/${orderId}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching order for payment receive:', error)
            throw error
        }
    }

    /**
     * Get payment receive statistics
     */
    static async getPaymentReceiveStatistics(params = {}) {
        try {
            const queryParams = new URLSearchParams({
                date_from: params.date_from || '',
                date_to: params.date_to || '',
                ...params
            })

            const response = await fetch(`${this.API_BASE_URL}payments/receive/statistics?${queryParams}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching payment receive statistics:', error)
            throw error
        }
    }

    /**
     * Update payment status
     */
    static async updatePaymentStatus(paymentId, statusData) {
        try {
            const response = await fetch(`${this.API_BASE_URL}payment_status_update/${paymentId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestOptions().headers
                },
                body: JSON.stringify(statusData)
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error updating payment status:', error)
            throw error
        }
    }

    /**
     * Format payment status
     */
    static formatPaymentStatus(status) {
        const statusMap = {
            'created': { label: 'Pending', color: 'warning', icon: 'clock' },
            'approved': { label: 'Approved', color: 'success', icon: 'check-circle' },
            'rejected': { label: 'Rejected', color: 'danger', icon: 'times-circle' }
        }
        return statusMap[status] || { label: 'Unknown', color: 'secondary', icon: 'question-circle' }
    }

    /**
     * Format currency
     */
    static formatCurrency(amount) {
        return new Intl.NumberFormat('en-BD', {
            style: 'currency',
            currency: 'BDT',
            minimumFractionDigits: 2
        }).format(amount || 0)
    }

    /**
     * Format date
     */
    static formatDate(dateString) {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    /**
     * Generate payment reference
     */
    static generatePaymentReference() {
        const timestamp = Date.now()
        const random = Math.floor(Math.random() * 1000)
        return `PAY${timestamp}${random}`
    }

    /**
     * Validate payment receive data
     */
    static validatePaymentReceiveData(data) {
        const errors = []

        if (!data.orderId) {
            errors.push('Order ID is required')
        }

        if (!data.amount || data.amount <= 0) {
            errors.push('Amount must be greater than 0')
        }

        if (!data.paymentType) {
            errors.push('Payment type is required')
        }

        if (data.amount && data.remainingAmount && data.amount > data.remainingAmount) {
            errors.push(`Amount cannot exceed remaining amount (${this.formatCurrency(data.remainingAmount)})`)
        }

        return errors
    }

    /**
     * Get payment types
     */
    static getPaymentTypes() {
        return [
            { value: 'cash', label: 'Cash' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'mobile_banking', label: 'Mobile Banking' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'debit_card', label: 'Debit Card' },
            { value: 'check', label: 'Check' },
            { value: 'online_payment', label: 'Online Payment' },
            { value: 'other', label: 'Other' }
        ]
    }

    /**
     * Get payment categories
     */
    static getPaymentCategories() {
        return [
            { value: 'full_payment', label: 'Full Payment' },
            { value: 'partial_payment', label: 'Partial Payment' },
            { value: 'advance_payment', label: 'Advance Payment' },
            { value: 'installment', label: 'Installment' },
            { value: 'refund', label: 'Refund' }
        ]
    }

    /**
     * Get transaction modes
     */
    static getTransactionModes() {
        return [
            { value: 'online', label: 'Online' },
            { value: 'offline', label: 'Offline' },
            { value: 'manual', label: 'Manual Entry' }
        ]
    }

    /**
     * Calculate payment summary
     */
    static calculatePaymentSummary(payments) {
        return payments.reduce((summary, payment) => {
            if (payment.status === 'approved') {
                summary.totalPaid += parseFloat(payment.amount) || 0
            } else if (payment.status === 'created') {
                summary.pendingAmount += parseFloat(payment.amount) || 0
            }
            summary.totalPayments += 1
            return summary
        }, { totalPaid: 0, pendingAmount: 0, totalPayments: 0 })
    }

    /**
     * Download payment file
     */
    static downloadPaymentFile(fileUrl, fileName) {
        const link = document.createElement('a')
        link.href = fileUrl
        link.download = fileName || 'payment_file'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    /**
     * Format file size
     */
    static formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    /**
     * Validate file upload
     */
    static validateFileUpload(file) {
        const errors = []
        const maxSize = 5 * 1024 * 1024 // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']

        if (file.size > maxSize) {
            errors.push('File size must be less than 5MB')
        }

        if (!allowedTypes.includes(file.type)) {
            errors.push('File type not supported. Please upload JPEG, PNG, GIF, PDF, or DOC files')
        }

        return errors
    }
}

export default PaymentReceiveService 